/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	BackgroundContainer,
	BlurredImage,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
	hasContent,
} from "@components/elements";
import { HeadingLevelContext } from "@components/context";
import { scrollIntoViewEffect } from "@components/utils/scroll.utils";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import { ResellerCardFragment, ResellersSearchSectionFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";
import { useCallback, useMemo, useRef } from "react";
import { ResellersSearchMolecule } from "./ResellersSearch.molecule";

export type ResellersSearchSectionProps = ResellersSearchSectionFragment & {
	skipRouter?: boolean;
	sectionIndex?: number;
	/** True when a page hero renders above this section — then even the first section is an h2, not the page h1. */
	hasHero?: boolean;
};

const descStyles = css({
	transform: "translateZ(0)",
	"& .title-desc-stack .cosmos-text": { color: "eds.text.primary" },
});

const bgImageStyles = css({
	position: "absolute",
	pointerEvents: "none",
	top: 0,
	bottom: 0,
	right: 0,
	left: 0,
	width: "100%",
	aspectRatio: "16 / 9",
});

export const ResellersSearchSection = ({
	config,
	blurredBackgroundImage,
	cards,
	cards2,
	cards3,
	cards4,
	sectionIndex,
	hasHero,
	...rest
}: ResellersSearchSectionProps): React.JSX.Element | null => {
	const sectionRef = useRef<HTMLDivElement>(null);
	const scrollToSection = useCallback(() => {
		scrollIntoViewEffect(sectionRef.current, 0);
	}, []);

	const combinedCards = useMemo(() => {
		const newArr: ResellerCardFragment[] = [];
		return newArr
			.concat(cards || [], cards2 || [], cards3 || [], cards4 || [])
			.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
	}, [cards, cards2, cards3, cards4]);
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(combinedCards?.length || rest?.noResultsLabel);
	if (!validHeading && !validContent) return null;

	// First ref-section with no hero above it → its title owns the page h1 (cards shift to h2).
	const isPageTitle = sectionIndex === 0 && !hasHero;

	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)} bgContain>
			<ResponsiveContainer>
				<SectionContainer {...extractSectionContainerProps(config)} ref={sectionRef}>
					<div className={bgImageStyles}>
						<BlurredImage
							image={blurredBackgroundImage}
							width={1920}
							height={1080}
							opacity={0.27}
							disableAutoplay
							blurPixels={150}
						/>
					</div>
					<VStack alignItems="stretch" gap="eds.dimension.96" className={cx("resellers-search-section", descStyles)}>
						{validHeading ? (
							<SectionHeading
								{...config?.heading}
								title={isPageTitle ? { ...config?.heading?.title, as: "h1" } : config?.heading?.title}
							/>
						) : null}
						{validContent ? (
							// Card names render h3; shift up to h2 when no section h2 title precedes them
							// (or when this section's title is the page h1).
							<HeadingLevelContext.Provider
								value={{ offset: isPageTitle ? -1 : hasContent(config?.heading?.title) ? 0 : -1 }}
							>
								<ResellersSearchMolecule {...rest} triggerScroll={scrollToSection} cards={combinedCards} />
							</HeadingLevelContext.Provider>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
